import {cn, cva} from 'platform/shared';

import type {ComponentPropsWithoutRef} from 'react';

export type TextEditorFrameProps = ComponentPropsWithoutRef<'div'> & {
  $isDisabled?: boolean;
  $isInvalid?: boolean;
  $isBorderless?: boolean;
  $isClipped?: boolean;
};

const textEditorFrameVariants = cva('relative min-w-0 rounded-[var(--radius-small)] border-solid', {
  variants: {
    isDisabled: {
      true: 'bg-[var(--color-palettes-white-10-40)]',
      false: 'bg-[var(--color-palettes-white-10-100)]',
    },
    isInvalid: {
      true: 'border-[var(--color-severity-danger)]',
      false: 'border-[var(--color-palettes-neutral-70-100)]',
    },
    isBorderless: {
      true: 'border-0',
      false: 'border',
    },
    isClipped: {
      true: 'overflow-hidden',
      false: 'overflow-visible',
    },
  },
  compoundVariants: [
    {
      isDisabled: false,
      isInvalid: false,
      isBorderless: false,
      className:
        'focus-within:border-[var(--color-severity-informational)] focus-within:[box-shadow:var(--shadow-active)]',
    },
    {
      isInvalid: true,
      className:
        'focus-within:border-[var(--color-severity-danger)] focus-within:[box-shadow:none]',
    },
    {
      isDisabled: true,
      className:
        'focus-within:border-[var(--color-palettes-neutral-70-40)] focus-within:[box-shadow:none]',
    },
    {
      isBorderless: true,
      className: 'focus-within:[box-shadow:none]',
    },
  ],
  defaultVariants: {
    isDisabled: false,
    isInvalid: false,
    isBorderless: false,
    isClipped: false,
  },
});

export function TextEditorFrame(props: TextEditorFrameProps) {
  const {$isDisabled, $isInvalid, $isBorderless, $isClipped, className, ...htmlProps} = props;

  return (
    <div
      {...htmlProps}
      className={cn(
        textEditorFrameVariants({
          isDisabled: $isDisabled,
          isInvalid: $isInvalid,
          isBorderless: $isBorderless,
          isClipped: $isClipped,
        }),
        className
      )}
    />
  );
}
